import { Injectable } from '@angular/core'
import { ActivatedRouteSnapshot } from '@angular/router'
import { forkJoin, of } from 'rxjs'
import { map, switchMap, take, catchError, defaultIfEmpty } from 'rxjs/operators'

import { ConfigurationService } from 'src/app/services/configuration.service'
import { CurrencyResolver } from './currency.resolver'
import { LocationResolver } from './location.resolver'

@Injectable({
    providedIn: 'root'
})
export class ConfigurationResolver  {

    constructor(
        private configurationService: ConfigurationService,
        private locationResolver: LocationResolver,
        private currencyResolver: CurrencyResolver,
    ) {}

    resolve(route: ActivatedRouteSnapshot) {
        // If config is already loaded, return it directly — no Subject needed.
        // This prevents the resolver from hanging on subsequent navigations,
        // where get() returns of(configuration) early without emitting on $configuration.
        const config$ = this.configurationService.configuration
            ? of(this.configurationService.configuration)
            : (() => {
                this.configurationService.get(route.data.isConfigTool).subscribe({
                    error: (err) => {
                        console.error('ConfigurationResolver: config HTTP request failed', err)
                        // Propagate the error through $configuration so take(1) below
                        // terminates and the outer catchError can handle it.
                        // Without this, $configuration never emits and the resolver hangs.
                        this.configurationService.$configuration.error(err)
                    }
                })
                return this.configurationService.$configuration.pipe(take(1))
            })()

        // Because resolvers happen asynchronously,
        // any resolvers that rely on configuration being loaded first
        // should go here instead of being added to the routing
        return config$.pipe(
            switchMap(() => forkJoin([
                this.locationResolver.resolve().pipe(defaultIfEmpty(null), catchError(() => of(null))),
                this.currencyResolver.resolve().pipe(defaultIfEmpty(null), catchError(() => of(null))),
            ])),
            map(() => ({
                success: true
            })),
            catchError(() => {
                return of({ success: false })
            })
        )
    }
}
